import React from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
// Line-art "add to a list" glyph — the product's voice. Never an emoji.
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import Tooltip from "@mui/material/Tooltip";

import Link2LineArt from "../../../assets/icons/Link2LineArt";
import TextView from "../../../components/TextView";
import { colors } from "../../../utils";
import { CAPTURE_TYPES, captureTypeLabel } from "../Capture.helpers";
import { captureDisplayName } from "../captureDisplayName";
import {
  CapturePreviewThumb,
  CaptureMediaPlayer,
  CaptureProvenance,
} from "./CapturePreview";
import { formatDate } from "./captureWheelPicker";
// SHARED capture card styleds (composed, never hand-rolled — G-21).
import {
  Card,
  CardBody,
  CardMeta,
  CardMetaStart,
  CardMetaSpacer,
  CardMetaDate,
  TypeChip,
  CardActions,
  MenuKebabBtn,
  DocLinkAnchor,
} from "../styles";
import { AddToSegmentButton, CardNotice } from "./CapturedOnWheel.styles";
import { writeCaptureDrag } from "./CaptureDragPayload";
import { captureRejectionReason, isCaptureInsertable } from "../captureToBlock";

/**
 * One attached-capture row. Composed from the SHARED capture card styleds
 * (Card / Thumb / CardBody / CardMeta / TypeChip / CardActions / MenuKebabBtn).
 *
 * Originally read-only ("this surface reads the wheel's captures back, it does
 * not manage them"). That scope note is superseded: the one management action a
 * wheel owner actually needs HERE is removing a capture they decided not to use
 * — sending them to a separate inbox screen to undo something they did on the
 * wheel is the papercut. It is exactly ONE action; deleting the capture outright
 * still lives on the Capture inbox, so this stays a read-plus-detach surface,
 * not a second inbox.
 */
const AttachedCaptureRow = ({
  item,
  onDetach,
  isDetaching,
  // CAPTURE_SEGMENT_DROP — all four are undefined while the flag is OFF, and
  // every use below is guarded, so the OFF render is byte-identical to today.
  canAddToSegment = false,
  onAddToSegment,
  wheelHasSegments = true,
  // ISP-9999 add-to-segment-autocreate. Whether the mount point can actually
  // create a Segment for a wheel that has none (only the builder, today) and
  // whether THIS row's create-and-add is currently in flight.
  canAutoCreateSegment = false,
  isAutoCreating = false,
  /**
   * QA-77 — this row now also renders inside the pane's "Full Library" section,
   * where a capture may belong to another wheel or to none at all.
   *
   * Defaults to TRUE so every existing call site (the wheel-scoped list, which
   * only ever holds attached rows) renders exactly as before. A library row
   * that is not attached HERE must not wear an "Attached" chip and must not
   * offer a Detach action that would either no-op or, worse, detach it from
   * somebody else's wheel — the control has to describe what it really does.
   */
  isAttachedHere = true,
}) => {
  // capture-attachedrow-use-displayname (ISP-9999): this row used to build its
  // own label from text_content||type-word and never read the shared
  // captureDisplayName() helper the Capture inbox (CaptureCard.jsx, QA-70)
  // already uses — so a wheel-side row showed "Document"/"Photo" instead of
  // the item's real title/filename and diverged from the inbox for the same
  // capture. Read the SAME helper here so both surfaces agree.
  const primaryText = captureDisplayName(item);
  const dateLabel = formatDate(
    isAttachedHere && item.attached_at ? item.attached_at : item?.created_at
  );

  const insertable = canAddToSegment && isCaptureInsertable(item);
  const rejection = canAddToSegment ? captureRejectionReason(item) : null;
  const typeLabel = captureTypeLabel(item.type);
  // ISP-9999 add-to-segment-autocreate — a wheel with no Segments is a dead
  // end ONLY where nothing can create one for it. Where the mount point CAN
  // (the builder), the button stays live and creates one on click instead.
  const isDeadEnd = !wheelHasSegments && !canAutoCreateSegment;
  const willAutoCreate = !wheelHasSegments && canAutoCreateSegment;

  /**
   * Drag SOURCE. Only an insertable capture is draggable: making a broken item
   * draggable and then refusing the drop is a worse experience than not
   * offering the gesture, and the reason is already printed on the card.
   */
  const dragProps =
    insertable
      ? {
          draggable: true,
          onDragStart: (e) => writeCaptureDrag(e.dataTransfer, item),
          "aria-roledescription": "draggable",
          style: { cursor: "grab" },
        }
      : {};

  return (
    <Card
      as="li"
      {...dragProps}
      data-capture-id={item.id}
      data-testid={`capture-row-${item.id}`}
    >
      <CapturePreviewThumb item={item} />
      <CardBody>
        <CardMeta>
          <CardMetaStart>
            <TypeChip>{captureTypeLabel(item.type)}</TypeChip>
          </CardMetaStart>
          {dateLabel && <CardMetaSpacer aria-hidden="true" />}
          {dateLabel && <CardMetaDate>{dateLabel}</CardMetaDate>}
        </CardMeta>

        <TextView
          variant="largeText"
          color={colors.black}
          dir="auto"
          style={{
            WebkitLineClamp: 2,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {primaryText || captureTypeLabel(item.type)}
        </TextView>

        <CaptureMediaPlayer item={item} />
        {/* QA-99 — where this capture came from, when it came from somewhere. */}
        <CaptureProvenance item={item} />

        {item.type === CAPTURE_TYPES.DOC && item.media_url && (
          <DocLinkAnchor href={item.media_url} target="_blank" rel="noreferrer">
            <DescriptionOutlinedIcon style={{ fontSize: 14 }} /> Open document
          </DocLinkAnchor>
        )}

        {/* CAPTURE_SEGMENT_DROP — the explicit control, "right underneath the
            captured item" (Kelly, verbatim). Rendered for EVERY capture, even
            one that cannot be inserted, so the reason is visible rather than
            the control silently vanishing. */}
        {canAddToSegment && (
          <>
            <AddToSegmentButton
              type="button"
              disabled={!insertable || isDeadEnd || isAutoCreating}
              onClick={() => onAddToSegment?.(item)}
              aria-label={
                isDeadEnd
                  ? `Add ${typeLabel} capture to a Segment — this Wheel has no Segments yet`
                  : willAutoCreate
                    ? `Add ${typeLabel} capture to a Segment — this Wheel has no Segments yet, so a new one will be created`
                    : `Add ${typeLabel} capture to a Segment`
              }
              data-testid={`add-to-segment-${item.id}`}
            >
              <PlaylistAddRoundedIcon style={{ fontSize: 16 }} aria-hidden="true" />
              {isAutoCreating
                ? "Creating…"
                : willAutoCreate
                  ? "Create Segment & Add"
                  : "Add to Segment"}
            </AddToSegmentButton>

            {/* The refusal is never silent. */}
            {rejection && (
              <CardNotice data-testid={`capture-notice-${item.id}`}>{rejection}</CardNotice>
            )}
            {!rejection && isDeadEnd && (
              <CardNotice data-testid={`capture-notice-${item.id}`}>
                This Wheel has no Segments yet. Add a Segment first.
              </CardNotice>
            )}
          </>
        )}

      </CardBody>

      <CardActions>
        {/* Detach is offered only where it is true: on a row attached to THIS
            wheel. In the Full Library section an unattached row has nothing to
            detach from.

            QA-1057 — Lucide link-2 line-art (broken link) for detach.

            gordon-0901-capture-panel-cluster: the green "Attached" text pill
            that used to sit in a CardAttachedFooter below is gone (Gordon
            does not want it — every row here is attached by definition).
            This Detach action is now the ONLY "attached here" signal on the
            row. */}
        {isAttachedHere && (
          <Tooltip title="Detach from Wheel">
            <MenuKebabBtn
              $compact
              type="button"
              aria-label={`Detach ${captureTypeLabel(item.type)} capture from Wheel`}
              disabled={isDetaching}
              onClick={() => onDetach?.(item)}
            >
              <Link2LineArt
                width={14}
                height={14}
                strokeWidth={1.15}
                color={colors.textGrey}
                data-testid="capture-detach-link-glyph"
              />
            </MenuKebabBtn>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};

export default AttachedCaptureRow;
